import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';

// Enhanced GraphQL queries with role-based filtering
const GET_SERVICE_TYPES = gql`
  query GetServiceTypes($userRole: UserRole) {
    serviceTypes(userRole: $userRole) {
      id
      title
      category
      isActive
      allowedRoles
      color
      showFinancialFields
      usePropertyFinancialFields
      departments
    }
  }
`;

const GET_SERVICE_TYPES_BY_ROLE = gql`
  query GetServiceTypesByRole($userRole: UserRole!) {
    serviceTypesByRole(userRole: $userRole) {
      id
      title
      category
      isActive
      allowedRoles
      color
      showFinancialFields
      usePropertyFinancialFields
      departments
    }
  }
`;

/**
 * Enhanced hook for service types with role-based filtering
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableRoleFilter - Whether to enable role-based filtering by default
 * @param {boolean} options.autoSelectFiltered - Whether to auto-select filtered service types
 * @returns {Object} Service types data and controls
 */
export function useServiceTypes(options = {}) {
  const { 
    enableRoleFilter = true, 
    autoSelectFiltered = true 
  } = options;
  
  const { user } = useAuth();
  
  // Admin users should never have role filtering enabled by default
  const shouldEnableRoleFilter = user?.role === 'ADMIN' ? false : enableRoleFilter;
  const shouldAutoSelect = user?.role === 'ADMIN' ? false : autoSelectFiltered;
  
  const [isRoleFilterEnabled, setIsRoleFilterEnabled] = useState(shouldEnableRoleFilter);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);

  // Use refs to track state and prevent infinite loops
  const hasAutoSelectedRef = useRef(false);
  const previousUserRoleRef = useRef(null);

  // Update role filter state when user role changes (ONLY when user actually changes)
  useEffect(() => {
    // Only trigger when user role actually changes, not on every re-render
    if (user?.role !== previousUserRoleRef.current) {
      previousUserRoleRef.current = user?.role;
      
      if (user?.role === 'ADMIN') {
        setIsRoleFilterEnabled(false);
      } else {
        setIsRoleFilterEnabled(shouldEnableRoleFilter);
      }
      
      // Only reset auto-selection flag when user actually changes (not on every render)
      hasAutoSelectedRef.current = false;
      // Clear selection when user changes to prevent conflicts
      setSelectedServiceTypes([]);
    }
  }, [user?.role]); // Remove shouldEnableRoleFilter to prevent circular updates

  // PERFORMANCE OPTIMIZATION: Use cache-first and delay secondary query
  // Query all service types (for when role filter is disabled)
  const { 
    data: allServiceTypesData, 
    loading: allLoading, 
    error: allError 
  } = useQuery(GET_SERVICE_TYPES, {
    skip: isRoleFilterEnabled && !!user?.role,
    fetchPolicy: 'cache-first', // Use cache-first for better performance
    notifyOnNetworkStatusChange: false
  });

  // Query role-filtered service types (for when role filter is enabled) - delayed
  const { 
    data: roleFilteredData, 
    loading: roleLoading, 
    error: roleError 
  } = useQuery(GET_SERVICE_TYPES_BY_ROLE, {
    variables: { userRole: user?.role },
    skip: !isRoleFilterEnabled || !user?.role || allLoading, // Wait for all service types first
    fetchPolicy: 'cache-first', // Use cache-first for better performance
    notifyOnNetworkStatusChange: false
  });

  // Determine which data to use based on filter state
  const serviceTypesData = useMemo(() => {
    if (isRoleFilterEnabled && user?.role) {
      return roleFilteredData;
    }
    return allServiceTypesData;
  }, [isRoleFilterEnabled, user?.role, roleFilteredData, allServiceTypesData]);

  // Get available service types (active only)
  const availableServiceTypes = useMemo(() => {
    if (isRoleFilterEnabled && user?.role) {
      // When role filter is enabled, backend already filters by role
      return serviceTypesData?.serviceTypesByRole?.filter(type => type.isActive) || [];
    } else {
      // When role filter is disabled, show ALL active service types (no role restrictions)
      return serviceTypesData?.serviceTypes?.filter(type => type.isActive) || [];
    }
  }, [serviceTypesData, isRoleFilterEnabled, user?.role]);

  // Get role-appropriate service types (for auto-selection)
  const roleAppropriateTypes = useMemo(() => {
    if (!user?.role) return [];
    
    // If we have role-filtered data, use it
    if (roleFilteredData?.serviceTypesByRole) {
      return roleFilteredData.serviceTypesByRole.filter(type => type.isActive);
    }
    
    // Otherwise filter from all service types
    return allServiceTypesData?.serviceTypes?.filter(type => 
      type.isActive && type.allowedRoles?.includes(user.role)
    ) || [];
  }, [user?.role, roleFilteredData, allServiceTypesData]);

  // Get all service types that belong to any department (for "show all departments" feature)
  const allDepartmentServiceTypes = useMemo(() => {
    if (!allServiceTypesData?.serviceTypes) return [];
    
    return allServiceTypesData.serviceTypes.filter(type => 
      type.isActive && 
      type.departments && 
      type.departments.length > 0
    );
  }, [allServiceTypesData]);

  // Auto-select all departments if user preference is enabled (PRIORITY: runs before role-based selection)
  useEffect(() => {
    // Only run if:
    // 1. User has the preference enabled
    // 2. We haven't auto-selected yet
    // 3. No service types are currently selected
    // 4. We have department service types available
    // 5. All service types data is loaded
    // 6. Available service types are loaded (to respect role filtering if enabled)
    if (
      user?.showAllDepartmentsByDefault && 
      !hasAutoSelectedRef.current && 
      selectedServiceTypes.length === 0 && 
      allDepartmentServiceTypes.length > 0 &&
      allServiceTypesData?.serviceTypes &&
      availableServiceTypes.length > 0
    ) {
      // Filter department types to only those that are available (respects role filtering)
      const availableDepartmentTypeIds = allDepartmentServiceTypes
        .filter(type => availableServiceTypes.some(available => available.id === type.id))
        .map(type => type.id);
      
      // Only set if we have available department types
      if (availableDepartmentTypeIds.length > 0) {
        setSelectedServiceTypes(availableDepartmentTypeIds);
        hasAutoSelectedRef.current = true; // Mark that we've auto-selected
      }
    }
  }, [user?.showAllDepartmentsByDefault, allDepartmentServiceTypes.length, selectedServiceTypes.length, allServiceTypesData, availableServiceTypes.length]);

  // Auto-select role-appropriate service types when filter is enabled (only if all departments wasn't selected)
  useEffect(() => {
    // Only auto-select when:
    // 1. Role filter is enabled
    // 2. Auto-select is enabled
    // 3. We have role-appropriate types
    // 4. No current selection
    // 5. We haven't auto-selected yet (including all departments)
    // 6. User doesn't have "show all departments" enabled (to avoid conflicts)
    if (
      isRoleFilterEnabled && 
      shouldAutoSelect && 
      roleAppropriateTypes.length > 0 && 
      selectedServiceTypes.length === 0 && 
      !hasAutoSelectedRef.current &&
      !user?.showAllDepartmentsByDefault
    ) {
      const roleTypeIds = roleAppropriateTypes.map(type => type.id);
      setSelectedServiceTypes(roleTypeIds);
      hasAutoSelectedRef.current = true; // Mark that we've auto-selected
    }
  }, [isRoleFilterEnabled, shouldAutoSelect, user?.role, roleAppropriateTypes.length, user?.showAllDepartmentsByDefault, selectedServiceTypes.length]); // Only depend on length, not the full array



  // Handle toggle between role filter and all service types
  const handleRoleFilterToggle = (enabled) => {
    setIsRoleFilterEnabled(enabled);
    
    if (enabled && shouldAutoSelect && roleAppropriateTypes.length > 0) {
      // When enabling role filter, auto-select role-appropriate types
      const roleTypeIds = roleAppropriateTypes.map(type => type.id);
      setSelectedServiceTypes(roleTypeIds);
      hasAutoSelectedRef.current = true; // Mark that we've auto-selected
    } else if (!enabled) {
      // When disabling role filter, keep current selection but expand available options
      // No automatic deselection to preserve user's choices
    }
  };

  // Handle service type selection with proper state management
  const handleServiceTypeChange = (serviceTypeIds) => {
    setSelectedServiceTypes(serviceTypeIds);
    // Mark that user has made a manual selection
    hasAutoSelectedRef.current = true;
  };

  // Handle individual service type toggle
  const handleServiceTypeToggle = (typeId) => {
    setSelectedServiceTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(id => id !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
    // Mark that user has made a manual selection
    hasAutoSelectedRef.current = true;
  };

  // Get selected service type objects
  const selectedServiceTypeObjects = useMemo(() => {
    return availableServiceTypes.filter(type => 
      selectedServiceTypes.includes(type.id)
    );
  }, [availableServiceTypes, selectedServiceTypes]);

  // Loading state
  const loading = isRoleFilterEnabled ? roleLoading : allLoading;
  
  // Error state
  const error = isRoleFilterEnabled ? roleError : allError;

  // Filter status information
  const filterInfo = useMemo(() => {
    const totalTypes = allServiceTypesData?.serviceTypes?.filter(type => type.isActive)?.length || 0;
    const filteredTypes = availableServiceTypes.length;
    const selectedCount = selectedServiceTypes.length;
    
    return {
      isFiltered: isRoleFilterEnabled,
      totalAvailable: filteredTypes,
      totalInSystem: totalTypes,
      selectedCount,
      userRole: user?.role || null,
      hasRoleBasedRestriction: filteredTypes < totalTypes
    };
  }, [isRoleFilterEnabled, availableServiceTypes.length, selectedServiceTypes.length, user?.role, allServiceTypesData]);

  return {
    // Data
    availableServiceTypes,
    selectedServiceTypes,
    selectedServiceTypeObjects,
    roleAppropriateTypes,
    
    // State
    isRoleFilterEnabled,
    loading,
    error,
    filterInfo,
    
    // Actions - use the same function for consistency
    setSelectedServiceTypes: handleServiceTypeChange,
    toggleServiceType: handleServiceTypeToggle,
    setRoleFilterEnabled: handleRoleFilterToggle,
    
    // Utilities
    isServiceTypeSelected: (typeId) => selectedServiceTypes.includes(typeId),
    clearSelection: () => handleServiceTypeChange([]),
    selectAllAvailable: () => handleServiceTypeChange(availableServiceTypes.map(type => type.id)),
    selectRoleAppropriate: () => handleServiceTypeChange(roleAppropriateTypes.map(type => type.id))
  };
} 